/**
 * Generato automaticamente.
 * framework-cmp-domande
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient.ts';
import type { ErrorType } from '../../../client/apiClient.ts';
import type { ComunicazioneDTO } from '../schemas/comunicazioneDTO.ts';
import type { GetFrontComunicazioniNonLetteCountParams } from '../schemas/getFrontComunicazioniNonLetteCountParams.ts';
import type { GetFrontRichiesteIdTipiComunicazioneParams } from '../schemas/getFrontRichiesteIdTipiComunicazioneParams.ts';
import type { TipoComunicazioneDTO } from '../schemas/tipoComunicazioneDTO.ts';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * @summary Segna come letta una Comunicazione ricevuta dall'utente loggato
 */
export const putFrontRichiesteIdComunicazioniIdSegnaLetta = (
  idRichiesta: string,
  idComunicazione: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cmp-domande/richieste/${idRichiesta}/comunicazioni/${idComunicazione}/segna-letta`,
      method: 'put',
    },
    options,
  );
};

export const getPutFrontRichiesteIdComunicazioniIdSegnaLettaMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putFrontRichiesteIdComunicazioniIdSegnaLetta>>,
    TError,
    { idRichiesta: string; idComunicazione: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putFrontRichiesteIdComunicazioniIdSegnaLetta>>,
  TError,
  { idRichiesta: string; idComunicazione: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putFrontRichiesteIdComunicazioniIdSegnaLetta>>,
    { idRichiesta: string; idComunicazione: string }
  > = props => {
    const { idRichiesta, idComunicazione } = props ?? {};

    return putFrontRichiesteIdComunicazioniIdSegnaLetta(
      idRichiesta,
      idComunicazione,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type PutFrontRichiesteIdComunicazioniIdSegnaLettaMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof putFrontRichiesteIdComunicazioniIdSegnaLetta>>
  >;

export type PutFrontRichiesteIdComunicazioniIdSegnaLettaMutationError =
  ErrorType<unknown>;

/**
 * @summary Segna come letta una Comunicazione ricevuta dall'utente loggato
 */
export const usePutFrontRichiesteIdComunicazioniIdSegnaLetta = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putFrontRichiesteIdComunicazioniIdSegnaLetta>>,
    TError,
    { idRichiesta: string; idComunicazione: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPutFrontRichiesteIdComunicazioniIdSegnaLettaMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * @summary Ricerca i Tipo Comunicazione di una determinata tipologia a livello di Richiesta
 */
export const getFrontRichiesteIdTipiComunicazione = (
  id: string,
  params?: GetFrontRichiesteIdTipiComunicazioneParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<TipoComunicazioneDTO[]>(
    {
      url: `/framework-cmp-domande/richieste/${id}/tipi-comunicazione`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetFrontRichiesteIdTipiComunicazioneQueryKey = (
  id: string,
  params?: GetFrontRichiesteIdTipiComunicazioneParams,
) =>
  [
    `/framework-cmp-domande/richieste/${id}/tipi-comunicazione`,
    ...(params ? [params] : []),
  ] as const;

export const getGetFrontRichiesteIdTipiComunicazioneQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdTipiComunicazione>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  params?: GetFrontRichiesteIdTipiComunicazioneParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdTipiComunicazione>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRichiesteIdTipiComunicazione>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontRichiesteIdTipiComunicazioneQueryKey(id, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRichiesteIdTipiComunicazione>>
  > = ({ signal }) =>
    getFrontRichiesteIdTipiComunicazione(id, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetFrontRichiesteIdTipiComunicazioneQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRichiesteIdTipiComunicazione>>
>;
export type GetFrontRichiesteIdTipiComunicazioneQueryError = ErrorType<unknown>;

/**
 * @summary Ricerca i Tipo Comunicazione di una determinata tipologia a livello di Richiesta
 */
export const useGetFrontRichiesteIdTipiComunicazione = <
  TData = Awaited<ReturnType<typeof getFrontRichiesteIdTipiComunicazione>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  params?: GetFrontRichiesteIdTipiComunicazioneParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRichiesteIdTipiComunicazione>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontRichiesteIdTipiComunicazioneQueryOptions(
    id,
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * @summary Ricerca puntuale Comunicazione da FrontOffice
 */
export const getFrontComunicazioniId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<ComunicazioneDTO>(
    {
      url: `/framework-cmp-domande/comunicazioni/${id}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontComunicazioniIdQueryKey = (id: string) =>
  [`/framework-cmp-domande/comunicazioni/${id}`] as const;

export const getGetFrontComunicazioniIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontComunicazioniId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontComunicazioniId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontComunicazioniId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontComunicazioniIdQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontComunicazioniId>>
  > = ({ signal }) => getFrontComunicazioniId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetFrontComunicazioniIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontComunicazioniId>>
>;
export type GetFrontComunicazioniIdQueryError = ErrorType<unknown>;

/**
 * @summary Ricerca puntuale Comunicazione da FrontOffice
 */
export const useGetFrontComunicazioniId = <
  TData = Awaited<ReturnType<typeof getFrontComunicazioniId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontComunicazioniId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontComunicazioniIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * @summary Cancella una Comunicazione
 */
export const deleteFrontComunicazioniId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    { url: `/framework-cmp-domande/comunicazioni/${id}`, method: 'delete' },
    options,
  );
};

export const getDeleteFrontComunicazioniIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteFrontComunicazioniId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteFrontComunicazioniId>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteFrontComunicazioniId>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteFrontComunicazioniId(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteFrontComunicazioniIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteFrontComunicazioniId>>
>;

export type DeleteFrontComunicazioniIdMutationError = ErrorType<unknown>;

/**
 * @summary Cancella una Comunicazione
 */
export const useDeleteFrontComunicazioniId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteFrontComunicazioniId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getDeleteFrontComunicazioniIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Se valorizzato idRichiesta il conteggio e' ristretto a quella Richiesta, altrimenti e' complessivo
 * @summary Conteggio delle Comunicazioni non lette dall'utente loggato
 */
export const getFrontComunicazioniNonLetteCount = (
  params?: GetFrontComunicazioniNonLetteCountParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<number>(
    {
      url: `/framework-cmp-domande/comunicazioni/non-lette/count`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetFrontComunicazioniNonLetteCountQueryKey = (
  params?: GetFrontComunicazioniNonLetteCountParams,
) =>
  [
    `/framework-cmp-domande/comunicazioni/non-lette/count`,
    ...(params ? [params] : []),
  ] as const;

export const getGetFrontComunicazioniNonLetteCountQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontComunicazioniNonLetteCount>>,
  TError = ErrorType<unknown>,
>(
  params?: GetFrontComunicazioniNonLetteCountParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontComunicazioniNonLetteCount>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontComunicazioniNonLetteCount>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontComunicazioniNonLetteCountQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontComunicazioniNonLetteCount>>
  > = ({ signal }) =>
    getFrontComunicazioniNonLetteCount(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetFrontComunicazioniNonLetteCountQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontComunicazioniNonLetteCount>>
>;
export type GetFrontComunicazioniNonLetteCountQueryError = ErrorType<unknown>;

/**
 * @summary Conteggio delle Comunicazioni non lette dall'utente loggato
 */
export const useGetFrontComunicazioniNonLetteCount = <
  TData = Awaited<ReturnType<typeof getFrontComunicazioniNonLetteCount>>,
  TError = ErrorType<unknown>,
>(
  params?: GetFrontComunicazioniNonLetteCountParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontComunicazioniNonLetteCount>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontComunicazioniNonLetteCountQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * @summary Scarica un allegato della comunicazione
 */
export const getFrontComunicazioniAllegatiId = (
  idAllegato: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<string>(
    {
      url: `/framework-cmp-domande/comunicazioni/allegati/${idAllegato}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontComunicazioniAllegatiIdQueryKey = (
  idAllegato: string,
) => [`/framework-cmp-domande/comunicazioni/allegati/${idAllegato}`] as const;

export const getGetFrontComunicazioniAllegatiIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontComunicazioniAllegatiId>>,
  TError = ErrorType<unknown>,
>(
  idAllegato: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontComunicazioniAllegatiId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontComunicazioniAllegatiId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontComunicazioniAllegatiIdQueryKey(idAllegato);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontComunicazioniAllegatiId>>
  > = ({ signal }) =>
    getFrontComunicazioniAllegatiId(idAllegato, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!idAllegato, ...queryOptions };
};

export type GetFrontComunicazioniAllegatiIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontComunicazioniAllegatiId>>
>;
export type GetFrontComunicazioniAllegatiIdQueryError = ErrorType<unknown>;

/**
 * @summary Scarica un allegato della comunicazione
 */
export const useGetFrontComunicazioniAllegatiId = <
  TData = Awaited<ReturnType<typeof getFrontComunicazioniAllegatiId>>,
  TError = ErrorType<unknown>,
>(
  idAllegato: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontComunicazioniAllegatiId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontComunicazioniAllegatiIdQueryOptions(
    idAllegato,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
